/**
 * Custom Toast Component
 *
 * This component provides a standardized way to show toast notifications
 * with consistent styling and positioning.
 */
import { addToast, cn } from '@heroui/react';
import { ReactNode } from 'react';

/**
 * Interface for success toast configuration
 */
export interface SuccessToastConfig {
    /** Title of the toast */
    title: string;
    /** Description of the toast */
    description: string;
    /** Additional content to display at the bottom of the toast */
    endContent?: ReactNode;
}

/**
 * Shows a success toast notification
 * @param config Toast configuration
 */
export function showSuccessToast(config: SuccessToastConfig) {
    addToast({
        title: config.title,
        description: config.description,
        classNames: {
            base: cn([
                'bg-default-50 shadow-sm dark:bg-background',
                'border-1',
                'relative before:absolute before:z-10 before:content-[""]',
                'before:bottom-[-1px] before:left-0 before:top-[-1px] before:w-1',
                'rounded-l-none border-l-0',
                'min-w-[350px] max-w-[450px]',
                'rounded-md',
                'flex flex-col items-start',
                'border-success-200 before:bg-success dark:border-success-200',
            ]),
            icon: cn(['h-6 w-6 fill-current']),
            closeButton: cn(['absolute right-1 top-1 p-0 text-default-400 hover:text-default-600']),
        },
        endContent: config.endContent,
        color: 'success',
        hideCloseButton: false,
    });
}

/**
 * Shows an error toast notification
 * @param title Title of the toast
 * @param description Description of the toast
 */
export function showErrorToast(title: string, description: string) {
    addToast({
        title,
        description,
        classNames: {
            base: cn([
                'bg-default-50 shadow-sm dark:bg-background',
                'border-1',
                'relative before:absolute before:z-10 before:content-[""]',
                'before:bottom-[-1px] before:left-0 before:top-[-1px] before:w-1',
                'rounded-l-none border-l-0',
                'min-w-[350px] max-w-[450px]',
                'rounded-md',
                'flex flex-col items-start',
                'border-danger-200 before:bg-danger dark:border-danger-200',
            ]),
            icon: cn(['h-6 w-6 fill-current']),
            closeButton: cn(['absolute right-1 top-1 p-0 text-default-400 hover:text-default-600']),
        },
        color: 'danger',
        hideCloseButton: false,
    });
}

/**
 * Shows an info toast notification
 * @param title Title of the toast
 * @param description Description of the toast
 */
export function showInfoToast(title: string, description: string) {
    addToast({
        title,
        description,
        classNames: {
            base: cn([
                'bg-default-50 shadow-sm dark:bg-background',
                'border-1',
                'relative before:absolute before:z-10 before:content-[""]',
                'before:bottom-[-1px] before:left-0 before:top-[-1px] before:w-1',
                'rounded-l-none border-l-0',
                'min-w-[350px] max-w-[450px]',
                'rounded-md',
                'flex flex-col items-start',
                'border-primary-200 before:bg-primary dark:border-primary-200',
            ]),
            icon: cn(['h-6 w-6 fill-current']),
            closeButton: cn(['absolute right-1 top-1 p-0 text-default-400 hover:text-default-600']),
        },
        color: 'primary',
        hideCloseButton: false,
    });
}
